import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Button, CardBody, Row } from 'reactstrap';

import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import AddressStore from '../../../../store/AddressStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import RequestState from '../../../../types/RequestState';
import globalTranslations from '../../../../i18n/globalTranslations';
import { labels } from '../../../../util/formLabels';
import { normalizeBooleanField } from '../../../../util/checkoutFormUtils';
import AddressCard from '../AddressCard';
import CustomCol from '../../../bootstrap/CustomCol';
import CheckoutOrderExtrasForm from '../CheckoutOrderExtrasForm';
import CheckoutRegistration from '../CheckoutRegistration';
import CheckoutYourReferenceForm from '../CheckoutYourReferenceForm';
import RecipientCheckoutModal from '../RecipientCheckoutModal';
import RecipientSeparateDeliveryCard from '../RecipientSeparateDeliveryCard';
import Switch from '../../../common/Switch';
import { useDeliveryAddressBookActions } from '../useDeliveryAddressBookActions';
import { useRecipientCheckoutForms } from '../useRecipientCheckoutForms';

/** Below Bootstrap `md` the billing/delivery columns stack; Row stretch no longer equalizes card heights. */
const STACKED_ADDRESS_COLUMNS_MEDIA = '(max-width: 767.98px)';

const CheckoutRecipientLoggedInLayout = ({
  checkoutStore,
  accountStore,
  addressStore,
  onOrderUpdate,
  intl,
}) => {
  const { order } = checkoutStore;
  const isB2B = order.isB2BCheckout;
  const forms = useRecipientCheckoutForms(order);

  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);

  const billingCardStretchRef = useRef(null);
  const deliveryCardStretchRef = useRef(null);

  const {
    deliveryAddressDropdownField,
    deliveryAddressDropdownFormField,
    deliveryDropdownOptions,
    commitDeliveryAddressDropdownSelection,
    confirmDeliveryAddressModal,
    prepareManualDeliveryAddressForSave,
    resetDeliveryMobXFormsFromOrder,
  } = useDeliveryAddressBookActions({
    order,
    checkoutStore,
    addressStore,
    onOrderUpdate,
    intl,
    forms,
  });

  const handleUpdate = ({ withoutTimer }) => {
    onOrderUpdate({ withoutTimer });
  };

  const isRetailerCustomer = Boolean(accountStore?.isRetailer);
  const isBillingCompany =
    isRetailerCustomer ||
    normalizeBooleanField(
      order.getCustomerField(FormFieldId.BILLING_IS_COMPANY).field?.value
    );
  const isDeliveryCompany =
    isRetailerCustomer ||
    normalizeBooleanField(
      order.getCustomerField(FormFieldId.DELIVERY_IS_COMPANY).field?.value
    );

  const billingAddressData = order.billingAddressData;
  const deliveryAddressData = order.deliveryAddressData;

  const separateDeliveryField = order.getCustomerField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  ).field;

  const hasSeparateDeliveryField = !!separateDeliveryField;

  const isCheckedSeparateDelivery = normalizeBooleanField(
    separateDeliveryField?.value
  );

  const separateDeliveryFormField = forms.billingForm.getField(
    FormFieldId.SEPARATE_DELIVERY_ADDRESS
  );

  const hasDeliveryAddressBookOptions =
    (deliveryAddressDropdownField?.options?.length ?? 0) > 0;

  const billingAddressHasMissingFields =
    billingAddressData.hasMissingRequiredFields;

  const billingColProps = hasSeparateDeliveryField
    ? { xs: 12, md: 6, lg: 6, xl: 6 }
    : { xs: 12, md: 12, lg: 12, xl: 12 };

  const deliveryColProps = { xs: 12, md: 6, lg: 6, xl: 6 };

  const handleSeparateDeliveryChange = (e) => {
    const newChecked = !!e.target.checked;
    if (separateDeliveryField) {
      separateDeliveryField.setValue(newChecked);
    }
    if (separateDeliveryFormField) {
      separateDeliveryFormField.setValue(newChecked ? '1' : null);
    }
    if (
      newChecked &&
      deliveryAddressDropdownField &&
      hasDeliveryAddressBookOptions
    ) {
      const current = deliveryAddressDropdownField.value;
      const noSelection = current == null || String(current).trim() === '';
      if (noSelection) {
        const opts = deliveryAddressDropdownField.options || [];
        const first = opts[0];
        if (first != null && first.value != null) {
          const raw = first.value;
          const numeric = typeof raw === 'number' ? raw : Number(raw);
          deliveryAddressDropdownField.setValue(
            Number.isNaN(numeric) ? raw : numeric
          );
          if (deliveryAddressDropdownFormField) {
            deliveryAddressDropdownFormField.setValue(String(raw));
          }
          const oneTimeField = order.customer_fields?.get(
            FormFieldId.ONE_TIME_DELIVERY_ADDRESS
          );
          if (oneTimeField) {
            oneTimeField.setValue(false);
          }
        }
      }
    }
    onOrderUpdate({ withoutTimer: true });
  };

  const billingTitle = isB2B ? (
    <FormattedMessage
      id="checkout.billingAddressTitle"
      defaultMessage="Billing address"
    />
  ) : (
    <FormattedMessage
      id="checkout.deliveryForm.title"
      defaultMessage="Buyer details"
    />
  );

  const deliveryTitle = (
    <FormattedMessage
      id="checkout.deliveryAddressTitle"
      defaultMessage="Delivery address"
    />
  );

  const closeDeliveryModal = () => {
    setDeliveryModalOpen(false);
    resetDeliveryMobXFormsFromOrder();
  };

  useLayoutEffect(() => {
    if (!hasSeparateDeliveryField) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(STACKED_ADDRESS_COLUMNS_MEDIA);

    const clearDeliveryMinHeight = () => {
      const el = deliveryCardStretchRef.current;
      if (el) {
        el.style.minHeight = '';
      }
    };

    const syncDeliveryStretchToBilling = () => {
      if (!mediaQuery.matches) {
        clearDeliveryMinHeight();
        return;
      }
      const billingEl = billingCardStretchRef.current;
      const deliveryEl = deliveryCardStretchRef.current;
      if (!billingEl || !deliveryEl) {
        return;
      }
      deliveryEl.style.minHeight = `${billingEl.offsetHeight}px`;
    };

    syncDeliveryStretchToBilling();

    const resizeObserver = new ResizeObserver(syncDeliveryStretchToBilling);
    if (billingCardStretchRef.current) {
      resizeObserver.observe(billingCardStretchRef.current);
    }

    const onMediaChange = () => syncDeliveryStretchToBilling();
    mediaQuery.addEventListener('change', onMediaChange);
    window.addEventListener('resize', syncDeliveryStretchToBilling);

    return () => {
      resizeObserver.disconnect();
      mediaQuery.removeEventListener('change', onMediaChange);
      window.removeEventListener('resize', syncDeliveryStretchToBilling);
      clearDeliveryMinHeight();
    };
  }, [hasSeparateDeliveryField, isCheckedSeparateDelivery]);

  const renderIncompleteHintInCard = () => (
    <p className="CheckoutRecipientLoggedInLayout__incomplete-hint">
      <FormattedMessage
        id="checkout.recipientAddressIncompleteHintInCard"
        defaultMessage="Required information is missing or invalid — use Edit below to update."
      />
    </p>
  );

  return (
    <CardBody
      className={classNames(
        'CheckoutRecipientLoggedInLayout',
        'CheckoutRecipientLoggedInLayout__body'
      )}
    >
      <Row className="CheckoutRecipientLoggedInLayout__address-row align-items-stretch">
        <CustomCol
          {...billingColProps}
          className="CheckoutRecipientLoggedInLayout__billing-column d-flex"
        >
          <div className="CheckoutRecipientLoggedInLayout__column-inner">
            <h3 className="CheckoutRecipientLoggedInLayout__card-header">
              {billingTitle}
            </h3>
            <div
              ref={billingCardStretchRef}
              className="CheckoutRecipientLoggedInLayout__card-stretch"
            >
              <AddressCard
                address={billingAddressData}
                isCompany={isBillingCompany}
                footerSlot={
                  billingAddressHasMissingFields
                    ? renderIncompleteHintInCard()
                    : null
                }
              />
            </div>
            <div className="CheckoutRecipientLoggedInLayout__actions">
              <Button
                color="secondary"
                type="button"
                onClick={() => setBillingModalOpen(true)}
                className="CheckoutRecipientLoggedInLayout__modify-button"
              >
                <FormattedMessage {...globalTranslations.editTitle} />
              </Button>
            </div>
          </div>
        </CustomCol>

        {hasSeparateDeliveryField && (
          <CustomCol
            {...deliveryColProps}
            className="CheckoutRecipientLoggedInLayout__delivery-column d-flex"
          >
            <div className="CheckoutRecipientLoggedInLayout__column-inner">
              <h3 className="CheckoutRecipientLoggedInLayout__card-header">
                {deliveryTitle}
              </h3>
              <div
                ref={deliveryCardStretchRef}
                className="CheckoutRecipientLoggedInLayout__card-stretch"
              >
                {isCheckedSeparateDelivery ? (
                  <AddressCard
                    address={deliveryAddressData}
                    isCompany={isDeliveryCompany}
                    headerSlot={
                      <Switch
                        checked={isCheckedSeparateDelivery}
                        onChange={handleSeparateDeliveryChange}
                        size="xs"
                        disabled={checkoutStore.shippingInfoIsLoading}
                      >
                        {labels.useDeliveryAddress}
                      </Switch>
                    }
                  />
                ) : (
                  <RecipientSeparateDeliveryCard
                    checked={isCheckedSeparateDelivery}
                    onChange={handleSeparateDeliveryChange}
                    disabled={checkoutStore.shippingInfoIsLoading}
                    label={labels.useDeliveryAddress}
                  />
                )}
              </div>
              <div
                className={classNames(
                  'CheckoutRecipientLoggedInLayout__actions',
                  !isCheckedSeparateDelivery &&
                    'CheckoutRecipientLoggedInLayout__actions--delivery-inactive'
                )}
              >
                {isCheckedSeparateDelivery ? (
                  <Button
                    color="secondary"
                    type="button"
                    onClick={() => setDeliveryModalOpen(true)}
                    className="CheckoutRecipientLoggedInLayout__modify-button"
                  >
                    <FormattedMessage {...globalTranslations.editTitle} />
                  </Button>
                ) : null}
              </div>
            </div>
          </CustomCol>
        )}
      </Row>

      <CheckoutOrderExtrasForm onOrderUpdate={handleUpdate} />
      <CheckoutYourReferenceForm onOrderUpdate={handleUpdate} />
      {order.show_registration && <CheckoutRegistration />}

      <RecipientCheckoutModal
        isOpen={billingModalOpen}
        toggle={() => setBillingModalOpen(false)}
        mode="billing"
        billingForm={forms.billingForm}
        deliveryForm={null}
        deliveryAddressDropdownField={null}
        deliveryAddressDropdownFormField={null}
        deliveryDropdownOptions={[]}
        onCommitDeliveryAddressDropdown={undefined}
        onOrderUpdate={onOrderUpdate}
        billingTitle={billingTitle}
        deliveryTitle={deliveryTitle}
        loading={false}
      />

      <RecipientCheckoutModal
        isOpen={deliveryModalOpen}
        toggle={closeDeliveryModal}
        mode="delivery"
        billingForm={null}
        deliveryForm={forms.deliveryForm}
        deliveryAddressDropdownField={deliveryAddressDropdownField}
        deliveryAddressDropdownFormField={deliveryAddressDropdownFormField}
        deliveryDropdownOptions={deliveryDropdownOptions}
        onCommitDeliveryAddressDropdown={commitDeliveryAddressDropdownSelection}
        onDeliveryModalConfirm={confirmDeliveryAddressModal}
        onPrepareManualDeliveryForSave={prepareManualDeliveryAddressForSave}
        onOrderUpdate={onOrderUpdate}
        billingTitle={billingTitle}
        deliveryTitle={deliveryTitle}
        loading={addressStore?.state === RequestState.LOADING}
      />
    </CardBody>
  );
};

CheckoutRecipientLoggedInLayout.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  accountStore: modelOf(AccountStore),
  addressStore: modelOf(AddressStore),
  onOrderUpdate: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

export default inject(
  'checkoutStore',
  'accountStore',
  'addressStore'
)(injectIntl(observer(CheckoutRecipientLoggedInLayout)));
